import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  Chip,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

function JsonTree({ data, depth = 0, path = '' }) {
  const [open, setOpen] = useState(depth < 2);
  const theme = useTheme();

  const handleCopy = (e, value) => {
    e.stopPropagation();
    navigator.clipboard.writeText(typeof value === 'string' ? value : JSON.stringify(value, null, 2));
  };

  if (data === null || data === undefined) {
    return <Typography component="span" color="text.secondary" fontFamily="monospace">null</Typography>;
  }
  if (typeof data === 'boolean') {
    return <Typography component="span" color={data ? 'success.main' : 'error.main'} fontFamily="monospace">{String(data)}</Typography>;
  }
  if (typeof data === 'number') {
    return <Typography component="span" color="info.main" fontFamily="monospace">{String(data)}</Typography>;
  }
  if (typeof data === 'string') {
    const display = data.length > 200 ? data.slice(0, 200) + '...' : data;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Typography component="span" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>&quot;{display}&quot;</Typography>
        <IconButton size="small" onClick={(e) => handleCopy(e, data)} sx={{ p: 0.25 }}>
          <CopyIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }
  if (Array.isArray(data)) {
    return (
      <Box>
        <Box onClick={() => setOpen((o) => !o)} sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography component="span" fontFamily="monospace" color="text.secondary">[{data.length} items]</Typography>
        </Box>
        <Collapse in={open}>
          <Box sx={{ pl: 2, borderLeft: 1, borderColor: 'divider', ml: 1 }}>
            {data.slice(0, 20).map((item, i) => (
              <Box key={i} sx={{ py: 0.25 }}>
                <Typography component="span" fontFamily="monospace" color="text.secondary" fontSize="0.85rem">[{i}]: </Typography>
                <JsonTree data={item} depth={depth + 1} path={`${path}[${i}]`} />
              </Box>
            ))}
            {data.length > 20 && <Typography fontFamily="monospace" color="text.secondary" fontSize="0.85rem">... {data.length - 20} more</Typography>}
          </Box>
        </Collapse>
      </Box>
    );
  }
  if (typeof data === 'object') {
    const keys = Object.keys(data);
    return (
      <Box>
        <Box onClick={() => setOpen((o) => !o)} sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography component="span" fontFamily="monospace" color="text.secondary">{`{${keys.length} keys}`}</Typography>
        </Box>
        <Collapse in={open}>
          <Box sx={{ pl: 2, borderLeft: 1, borderColor: 'divider', ml: 1 }}>
            {keys.slice(0, 50).map((k) => (
              <Box key={k} sx={{ py: 0.25 }}>
                <Typography component="span" fontFamily="monospace" fontWeight="bold" fontSize="0.85rem">&quot;{k}&quot;: </Typography>
                <JsonTree data={data[k]} depth={depth + 1} path={`${path}.${k}`} />
              </Box>
            ))}
            {keys.length > 50 && <Typography fontFamily="monospace" color="text.secondary" fontSize="0.85rem">... {keys.length - 50} more</Typography>}
          </Box>
        </Collapse>
      </Box>
    );
  }
  return <Typography component="span" fontFamily="monospace">{String(data)}</Typography>;
}

export default function ContextInspector({
  state = {}, variables = {}, executionPath = [], currentNode = null, streamEvents = [], nodeOutputs = {},
}) {
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const hasStreamEvents = Array.isArray(streamEvents) && streamEvents.length > 0;
  const hasNodeOutputs = nodeOutputs && typeof nodeOutputs === 'object' && Object.keys(nodeOutputs).length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 1, height: '100%', overflow: 'auto' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ minHeight: 36, mb: 1 }}>
        <Tab label="Variables" />
        <Tab label="State" />
        <Tab label="Execution Path" />
        {hasNodeOutputs && <Tab label="Node outputs" />}
        {hasStreamEvents && <Tab label="LG v2 Stream" />}
      </Tabs>
      {tab === 0 && (
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {Object.keys(variables).length === 0 ? (
            <Typography color="text.secondary" fontSize="0.9rem">No variables</Typography>
          ) : (
            <JsonTree data={variables} />
          )}
        </Box>
      )}
      {tab === 1 && (
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {Object.keys(state).length === 0 ? (
            <Typography color="text.secondary" fontSize="0.9rem">No state</Typography>
          ) : (
            <JsonTree data={state} />
          )}
        </Box>
      )}
      {tab === 2 && (
        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
          {executionPath.length === 0 ? (
            <Typography color="text.secondary" fontSize="0.9rem">No nodes executed yet</Typography>
          ) : (
            executionPath.map((entry, i) => {
              const rawNodeId = typeof entry === 'object' ? entry.node_id : entry;
              const rawNodeType = typeof entry === 'object' ? entry.node_type : '';
              const rawStatus = typeof entry === 'object' ? entry.status : '';
              const nodeId = typeof rawNodeId === 'string' ? rawNodeId : (rawNodeId != null ? String(rawNodeId) : '');
              const nodeType = typeof rawNodeType === 'string' ? rawNodeType : (rawNodeType != null ? String(rawNodeType) : '');
              const status = typeof rawStatus === 'string' ? rawStatus : (rawStatus != null ? String(rawStatus) : '');
              const isCurrent = nodeId === currentNode;
              return (
                <ListItem key={i} sx={{ py: 0.25, bgcolor: isCurrent ? 'action.selected' : 'transparent', borderRadius: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontFamily="monospace" fontWeight={isCurrent ? 600 : 400}>{nodeId}</Typography>
                        {nodeType && <Chip label={nodeType} size="small" />}
                        {status && <Chip label={status} size="small" color={status === 'completed' ? 'success' : status === 'error' ? 'error' : 'default'} />}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })
          )}
        </List>
      )}
      {hasNodeOutputs && tab === 3 && (
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          <JsonTree data={nodeOutputs} />
        </Box>
      )}
      {hasStreamEvents && tab === (hasNodeOutputs ? 4 : 3) && (
        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
          {streamEvents.slice(-80).map((ev, i) => {
            const meta = ev?.langgraph_v2 || {};
            const eventType = meta.event || ev?.event_type || ev?.type || 'event';
            const name = meta.name || ev?.node_id || '';
            return (
              <ListItem key={`${eventType}-${name}-${i}`} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemText
                  primary={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip label={eventType} size="small" />
                      {name && <Chip label={name} size="small" variant="outlined" />}
                      {meta.run_id && <Chip label={`run ${String(meta.run_id).slice(0, 8)}`} size="small" variant="outlined" />}
                    </Box>
                  )}
                  secondary={meta.metadata && Object.keys(meta.metadata).length ? (
                    <Box sx={{ mt: 0.5 }}>
                      <JsonTree data={meta.metadata} />
                    </Box>
                  ) : null}
                />
              </ListItem>
            );
          })}
        </List>
      )}
      {currentNode && (
        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">Current node: </Typography>
          <Chip label={typeof currentNode === 'string' ? currentNode : String(currentNode ?? '')} size="small" color="primary" />
        </Box>
      )}
    </Paper>
  );
}
